/* eslint-disable react/prop-types */
import React, { FC, useState } from 'react'
import { DashboardCard } from './components/DashboardCard'
import {
    Content,
    DashboardDivContainer,
    DashboardsDiv,
    DashboardsList,
    DashboardsScroll,
    DashboardTitle,
    ShowDiv,
    TextHeader
} from './styles'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { selectMobileDashboardTab, selectOpen, setMobileDashboardTab } from '../../ducks/navigationSlice'
import Breadcrumb from '../Breadcrumb'
import _ from 'lodash'
import DateService from 'live/services/date'
import { Link, Tab, Tabs } from '@mui/material'
import LinkIcon from '@mui/icons-material/Link'
import Utils from 'live/lib/utils'
import moment from 'moment'

import styled from 'styled-components'

interface DashboardsPageProps {
    homeId: string
    selectedDashboardNode: DashboardNode
    sortOrder: Array<string>
}

export const DashboardsPage: FC<DashboardsPageProps> = ({ homeId, selectedDashboardNode, sortOrder }) => {
    const open = useAppSelector(state => selectOpen(state))
    const [showMore, setShowMore] = useState<Map<string, boolean>>(new Map<string, boolean>())

    React.useEffect(() => {
        getDashboards(selectedDashboardNode, sortOrder).map((item, index) => {
            setShowMore(state => state.set(item.label, false))
        })
    }, [selectedDashboardNode])

    const getDashboards = (selectedNode: DashboardNode, sortOrder: Array<string>): DashboardType[] => {
        if (!selectedNode || !selectedNode.dashboards) return []

        //Ordering the dashboards
        let types = _.uniq(selectedNode.dashboards.map(x => x.metadata?.dashboardCategory)).sort()
        let orderedTypes = sortOrder && sortOrder.length > 0 ? _.clone(sortOrder) : types
        if (sortOrder && sortOrder.length > 0) {
            orderedTypes = orderedTypes.filter(x => types.includes(x))
            orderedTypes.push(...types.filter(x => !orderedTypes.includes(x)))
        }
        return orderedTypes.map(x => {
            return {
                label: x,
                dashboards: selectedNode.dashboards
                    .filter(y => y.metadata?.dashboardCategory === x)
                    .map(dashboard => {
                        return {
                            ...dashboard,
                            icon: dashboard.label
                        }
                    })
            }
        })
    }

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    })

    const renderInfo = (title: string, value: string, border = true) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: Utils.isMobile() ? '10px' : '0px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', padding: '0px 10px 0px 10px' }}>
                    <span
                        style={{
                            textTransform: 'uppercase',
                            fontSize: Utils.isMobile() ? '16px' : '10px',
                            color: Utils.isMobile() ? 'var(--gray-700)' : 'var(--gray-600)',
                            fontWeight: 400,
                            lineHeight: Utils.isMobile() ? '23px' : '12px'
                        }}
                    >
                        {title}
                    </span>
                    <span
                        style={{
                            fontSize: Utils.isMobile() ? '16px' : '12px',
                            color: Utils.isMobile() ? 'var(--gray-700)' : 'var(--gray-600)',
                            fontWeight: 600
                        }}
                    >
                        {value ?? '-'}
                    </span>
                </div>
                <div
                    style={{
                        borderRight: border ? '1px solid var(--gray-300)' : 'none',
                        height: '80%',
                        width: '1px',
                        lineHeight: '15px'
                    }}
                />
            </div>
        )
    }

    const renderWithLink = (url: string, renderLink: boolean, title: string, value: string) => {
        return renderLink ? (
            <Link target={'_blank'} href={url} underline={'hover'} color={'inherit'}>
                {renderInfo(title, value)}
            </Link>
        ) : (
            renderInfo(title, value)
        )
    }

    const renderDesktop = () => {
        return (
            <Content sidebarOpen={open}>
                <Breadcrumb homeId={homeId} padding={10} />
                {homeId !== selectedDashboardNode?.id && (
                    <TextHeader>
                        {selectedDashboardNode.assetId ? (
                            <Link
                                href={`#/wells/${selectedDashboardNode.assetId}`}
                                underline="hover"
                                color="inherit"
                                target={'_blank'}
                            >
                                {selectedDashboardNode.pageName}
                            </Link>
                        ) : (
                            selectedDashboardNode.pageName
                        )}
                        {selectedDashboardNode.assetName && (
                            <div style={{ display: 'flex' }}>
                                {selectedDashboardNode.assetName &&
                                    renderWithLink(
                                        `#/wells/${selectedDashboardNode.assetId}`,
                                        !!selectedDashboardNode.assetId,
                                        'Wellbore',
                                        selectedDashboardNode.assetName
                                    )}
                                {selectedDashboardNode.rigName &&
                                    renderWithLink(
                                        `#/rig/${selectedDashboardNode.rigAssetId}`,
                                        !!selectedDashboardNode.rigAssetId,
                                        'Rig Name',
                                        selectedDashboardNode.rigName
                                    )}
                                {selectedDashboardNode.interventionId &&
                                    renderWithLink(
                                        `#/wells/${selectedDashboardNode.assetId}/interventions/${selectedDashboardNode.interventionId}`,
                                        !!selectedDashboardNode.assetId && !!selectedDashboardNode.interventionId,
                                        'Intervention',
                                        selectedDashboardNode.interventionDescription
                                    )}
                                {selectedDashboardNode.totalDailyCost ? (
                                    renderInfo('Daily Cost', formatter.format(selectedDashboardNode.totalDailyCost))
                                ) : (
                                    <></>
                                )}
                                {selectedDashboardNode.totalMudCost ? (
                                    renderInfo('Mud Cost', formatter.format(selectedDashboardNode.totalMudCost))
                                ) : (
                                    <></>
                                )}
                                {renderInfo('Event', selectedDashboardNode.event)}
                                {renderInfo('Rig State', selectedDashboardNode.rigState)}
                                {renderInfo(
                                    'Range Depth',
                                    selectedDashboardNode.mdIn && selectedDashboardNode.mdOut
                                        ? selectedDashboardNode.mdIn < 0
                                            ? '0'
                                            : selectedDashboardNode.mdIn.toFixed(2) +
                                              ' to ' +
                                              selectedDashboardNode.mdOut.toFixed(2)
                                        : '-'
                                )}
                                {renderInfo(
                                    'Time',
                                    selectedDashboardNode.dateIn && selectedDashboardNode.dateOut
                                        ? moment(selectedDashboardNode.dateIn).format('YYYY-MM-DD') +
                                              ' to ' +
                                              moment(selectedDashboardNode.dateOut).format('YYYY-MM-DD')
                                        : '-',
                                    false
                                )}
                            </div>
                        )}
                    </TextHeader>
                )}
                <DashboardsScroll>
                    {getDashboards(selectedDashboardNode, sortOrder).map((item, index) => {
                        return (
                            <DashboardsDiv key={index}>
                                <DashboardDivContainer>
                                    <DashboardTitle>{item.label}</DashboardTitle>
                                    <DashboardsList>
                                        {item?.dashboards
                                            .slice(
                                                0,
                                                item?.dashboards.length > 5 && !showMore.get(item.label)
                                                    ? 4
                                                    : item?.dashboards.length
                                            )
                                            .map((dashboard, index) => (
                                                <DashboardCard
                                                    dashboard={dashboard}
                                                    key={index}
                                                    image={dashboard.label}
                                                    title={dashboard.label}
                                                    subTitle={dashboard.description}
                                                    url={`/#/dashboard/${dashboard.dashboardId}${
                                                        dashboard.filter ? dashboard.filter : ''
                                                    }`}
                                                    isActiveNode={selectedDashboardNode.active}
                                                />
                                            ))}
                                        <ShowDiv
                                            onClick={() => {
                                                setShowMore(
                                                    state =>
                                                        new Map<string, boolean>(
                                                            state.set(item.label, !state.get(item.label))
                                                        )
                                                )
                                            }}
                                        >
                                            {item?.dashboards.length > 5
                                                ? !showMore.get(item.label)
                                                    ? `Show more[${item?.dashboards.length - 4}]`
                                                    : 'Show less'
                                                : ''}
                                        </ShowDiv>
                                    </DashboardsList>
                                </DashboardDivContainer>
                            </DashboardsDiv>
                        )
                    })}
                </DashboardsScroll>
            </Content>
        )
    }

    const dispatch = useAppDispatch()
    const tab = useAppSelector(state => selectMobileDashboardTab(state))
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        dispatch(setMobileDashboardTab(newValue))
    }

    const renderMobile = () => {
        interface TabPanelProps {
            children?: React.ReactNode
            index: number
            value: number
        }

        function CustomTabPanel(props: TabPanelProps) {
            const { children, value, index, ...other } = props

            return (
                <div
                    role="tabpanel"
                    hidden={value !== index}
                    id={`simple-tabpanel-${index}`}
                    aria-labelledby={`simple-tab-${index}`}
                    {...other}
                    style={{
                        width: '100%'
                    }}
                >
                    {value === index && children}
                </div>
            )
        }

        function a11yProps(index: number) {
            return {
                id: `simple-tab-${index}`,
                'aria-controls': `simple-tabpanel-${index}`
            }
        }

        return (
            <Content sidebarOpen={open}>
                <div
                    style={{
                        borderBottom: '1px solid var(--gray-400)',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <Tabs value={tab} onChange={handleChange}>
                        <StyledTab
                            label="Information"
                            style={{ display: homeId !== selectedDashboardNode?.id ? 'inline-flex' : 'none' }}
                            {...a11yProps(0)}
                        />
                        <StyledTab label="Dashboards" {...a11yProps(1)} />
                    </Tabs>
                </div>
                <CustomTabPanel value={tab} index={0}>
                    <div style={{ padding: '12px 16px', width: '99%' }}>
                        {selectedDashboardNode.assetName &&
                            renderWithLink(
                                `#/wells/${selectedDashboardNode.assetId}`,
                                !!selectedDashboardNode.assetId,
                                'Wellbore',
                                selectedDashboardNode.assetName
                            )}
                        {selectedDashboardNode.rigName &&
                            renderWithLink(
                                `#/rig/${selectedDashboardNode.rigAssetId}`,
                                !!selectedDashboardNode.rigAssetId,
                                'Rig Name',
                                selectedDashboardNode.rigName
                            )}
                        {selectedDashboardNode.interventionId &&
                            renderWithLink(
                                `#/wells/${selectedDashboardNode.assetId}/interventions/${selectedDashboardNode.interventionId}`,
                                !!selectedDashboardNode.assetId && !!selectedDashboardNode.interventionId,
                                'Intervention',
                                selectedDashboardNode.interventionDescription
                            )}
                        {renderInfo('Event', selectedDashboardNode.event)}
                        {renderInfo(
                            'Daily Cost',
                            selectedDashboardNode.totalDailyCost
                                ? formatter.format(selectedDashboardNode.totalDailyCost)
                                : '-'
                        )}
                        {renderInfo(
                            'Mud Cost',
                            selectedDashboardNode.totalMudCost
                                ? formatter.format(selectedDashboardNode.totalMudCost)
                                : '-'
                        )}
                        {renderInfo('Rig State', selectedDashboardNode.rigState)}
                        {renderInfo(
                            'Range Depth',
                            selectedDashboardNode.mdIn && selectedDashboardNode.mdOut
                                ? `${
                                      selectedDashboardNode.mdIn < 0 ? '0' : selectedDashboardNode.mdIn.toFixed(2)
                                  } to ${selectedDashboardNode.mdOut.toFixed(2)}`
                                : '-'
                        )}
                        {renderInfo(
                            'Time',
                            selectedDashboardNode.dateIn && selectedDashboardNode.dateOut
                                ? `${moment(selectedDashboardNode.dateIn).format('YYYY-MM-DD')} to ${moment(
                                      selectedDashboardNode.dateOut
                                  ).format('YYYY-MM-DD')}`
                                : '-',
                            false
                        )}
                    </div>
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={1}>
                    <DashboardsScroll isMobile={true}>
                        {getDashboards(selectedDashboardNode, sortOrder).map((item, index) => {
                            return (
                                <DashboardsDiv key={index}>
                                    <DashboardDivContainer>
                                        <DashboardTitle hide={item.label.trim() === ''}>
                                            {item.label} dashboards
                                        </DashboardTitle>
                                        <DashboardsList>
                                            {item?.dashboards.map((dashboard, index) => (
                                                <DashboardCard
                                                    dashboard={dashboard}
                                                    key={index}
                                                    image={dashboard.label}
                                                    title={dashboard.label}
                                                    subTitle={dashboard.description}
                                                    url={`/#/dashboard/${dashboard.dashboardId}?${dashboard.filter}`}
                                                    isActiveNode={selectedDashboardNode.active}
                                                />
                                            ))}
                                        </DashboardsList>
                                    </DashboardDivContainer>
                                </DashboardsDiv>
                            )
                        })}
                    </DashboardsScroll>
                </CustomTabPanel>
            </Content>
        )
    }

    return <>{Utils.isMobile() ? renderMobile() : renderDesktop()}</>
}

const StyledTab = styled((props: any) => <Tab {...props} />)(() => ({
    fontSize: '12px !important',
    color: 'var(--gray-900) !important',
    '&.Mui-selected': {
        color: 'var(--blue-700) !important'
    },
    '&.Mui-focusVisible': {
        backgroundColor: 'var(--gray-500) !important'
    },
    '&MuiTabs-indicator': {
        backgroundColor: 'var(--blue-700) !important'
    }
}))
